import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ComponentProps, FC } from "react";

import { cn } from "@/lib/utils";

const variants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0 transition-[color,box-shadow]",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow-sm [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-sm [a&]:hover:bg-destructive/90",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        green:
          "border-transparent bg-green-100 text-green-800 [a&]:hover:bg-green-100/90",
        orange:
          "border-transparent bg-orange-100 text-orange-700 [a&]:hover:bg-orange-100/90",
        blue: "border-transparent bg-blue-100 text-blue-700 [a&]:hover:bg-blue-100/90",
        sky: "border-transparent bg-sky-100 text-sky-800 [a&]:hover:bg-sky-100/90",
        purple:
          "border-transparent bg-purple-100 text-purple-800 [a&]:hover:bg-purple-100/90",
        fuchsia:
          "border-transparent bg-fuchsia-100 text-fuchsia-800 [a&]:hover:bg-fuchsia-100/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type Props = ComponentProps<"span"> &
  VariantProps<typeof variants> & { asChild?: boolean };

export const Badge: FC<Props> = ({
  className,
  variant,
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(variants({ variant }), className)}
      {...props}
    />
  );
};
